import React, { useState, useEffect, useCallback } from 'react';
import { UploadCloud } from 'lucide-react';
import DocUploadModal from './DocUploadModal';

// Self-hiding "the office needs a document from you" banner, shown on the driver
// portal (mirrors DriverDetailsCard). Fetches this login's PENDING upload requests
// from driver-docs-sb; tapping Upload opens the shared DocUploadModal, which
// fulfils the request. Reports the outstanding count up for the tab red dot.
const API = '/.netlify/functions/driver-docs-sb';

export default function DocUploadRequestCard({ shift, onCountChange }) {
  const driverLogin = (shift?.driver || '').toUpperCase();
  const [requests, setRequests] = useState([]);
  const [uploadFor, setUploadFor] = useState(null);   // the request being fulfilled

  const report = useCallback((n) => { if (onCountChange) onCountChange(n); }, [onCountChange]);

  const load = useCallback(async () => {
    try {
      const d = await fetch(API, { method: 'POST', body: JSON.stringify({ action: 'getMyUploadRequests' }) }).then(r => r.json());
      if (d && d.success) { setRequests(d.requests || []); report((d.requests || []).length); }
    } catch { /* the portal must not break on this */ }
  }, [report]);

  useEffect(() => { if (driverLogin) load(); }, [driverLogin, load]);

  if (!requests.length) return null;

  return (
    <div style={{ width: '100%', marginBottom: 14 }}>
      <div style={{ padding: '14px 16px', borderRadius: 12, background: 'rgba(239,68,68,0.08)', border: '1px solid var(--hp-red)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <UploadCloud size={22} color="var(--hp-red)" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>
              {requests.length === 1 ? 'The office needs a document from you' : `The office needs ${requests.length} documents from you`}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>Please upload the requested document{requests.length > 1 ? 's' : ''} below.</div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {requests.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', borderRadius: 8, background: 'var(--hp-card)', border: '1px solid var(--hp-border)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{r.title}</div>
                {r.note && <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 2 }}>{r.note}</div>}
              </div>
              <button className="btn btn-primary btn-sm" onClick={() => setUploadFor(r)}>Upload</button>
            </div>
          ))}
        </div>
      </div>

      {uploadFor && (
        <DocUploadModal
          requestId={uploadFor.id}
          requestTitle={uploadFor.title}
          onClose={() => setUploadFor(null)}
          onDone={() => { setUploadFor(null); load(); }}
        />
      )}
    </div>
  );
}
